import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ActivityReportTable from '../components/reports/ActivityReportTable';
import AIProductivitySummary from '../components/dashboard/AIProductivitySummary';
import ProjectQualityTable from '../components/dashboard/ProjectQualityTable';
import QualityTrendChart from '../components/dashboard/QualityTrendChart';
import './ReportsPage.css';

type Tab = 'activity' | 'ai' | 'quality' | 'trend';
const VALID_TABS: Tab[] = ['activity', 'ai', 'quality', 'trend'];

const AI_RANGES = [
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: '1 Year', days: 365 },
];

export const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    initialTab && VALID_TABS.includes(initialTab) ? initialTab : 'activity'
  );
  const [aiDays, setAiDays] = useState(30);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'activity' ? {} : { tab });
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div className="reports-header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
          </svg>
        </div>
        <div>
          <h1>Reports</h1>
          <p>Detailed historical analysis, evidence, and exports — everything summarized on the Dashboard, in full.</p>
        </div>
      </div>

      <div className="reports-tabs">
        <button className={`reports-tab${activeTab === 'activity' ? ' active' : ''}`} onClick={() => selectTab('activity')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
          Activity Report
        </button>
        <button className={`reports-tab${activeTab === 'ai' ? ' active' : ''}`} onClick={() => selectTab('ai')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l1.9 5.8L20 10l-6.1 2.2L12 18l-1.9-5.8L4 10l6.1-2.2L12 2z"/></svg>
          AI Productivity
        </button>
        <button className={`reports-tab${activeTab === 'quality' ? ' active' : ''}`} onClick={() => selectTab('quality')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
          Project/Epic Quality
        </button>
        <button className={`reports-tab${activeTab === 'trend' ? ' active' : ''}`} onClick={() => selectTab('trend')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18 9l-5 5-4-4-4 4"/></svg>
          Execution Trend
        </button>
      </div>

      <div className="reports-content">
        {activeTab === 'activity' && <ActivityReportTable />}

        {activeTab === 'ai' && (
          <div className="reports-ai-tab">
            <div className="reports-range-toggle">
              {AI_RANGES.map(r => (
                <button
                  key={r.label}
                  className={aiDays === r.days ? 'active' : ''}
                  onClick={() => setAiDays(r.days)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <AIProductivitySummary days={aiDays} showFooterLink={false} />
          </div>
        )}

        {activeTab === 'quality' && <ProjectQualityTable limit={50} />}

        {activeTab === 'trend' && <QualityTrendChart />}
      </div>
    </div>
  );
};

export default ReportsPage;
